import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Eye, Printer, FileText,
  ChevronDown, ChevronUp, X, Upload,
} from 'lucide-react';
import { ServiceLayout } from '@/components/ServiceLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const PAGE_SIZES = {
  A4: { width: 210, height: 297, label: 'A4 (210 × 297 mm)' },
  A5: { width: 148, height: 210, label: 'A5 (148 × 210 mm)' },
};

// Match ServiceJobsList FY helpers exactly so users see the same range here
const getCurrentFY = () => {
  const t = new Date();
  return t.getMonth() >= 3 ? `${t.getFullYear()}-${t.getFullYear() + 1}` : `${t.getFullYear() - 1}-${t.getFullYear()}`;
};
const fyOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => `${y - i}-${y - i + 1}`);
};

const PlaceholderChip = ({ field, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(field.key)}
    data-testid={`ph-${field.key}`}
    className="px-2 py-1 bg-indigo-500 text-white text-[11px] rounded-md hover:bg-indigo-600 transition-colors m-0.5"
    title={`Insert {{${field.key}}}`}
  >
    {field.label}
  </button>
);

const PlaceholderCategory = ({ category, fields, onInsert, isExpanded, onToggle, testId }) => (
  <div className="border border-slate-200 rounded-lg mb-2 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
    >
      <span className="font-medium text-[12.5px] text-slate-700">{category}</span>
      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
    {isExpanded && (
      <div className="p-2 flex flex-wrap">
        {fields.map((f) => (
          <PlaceholderChip key={f.key} field={f} onClick={onInsert} />
        ))}
      </div>
    )}
  </div>
);

export default function ServiceMultiReports({ user, onLogout }) {
  const [templates, setTemplates] = React.useState([]);
  const [placeholders, setPlaceholders] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState(null);
  const [expandedCategories, setExpandedCategories] = React.useState({});
  const [activeEditorField, setActiveEditorField] = React.useState('body');

  // Preview
  const [showPreview, setShowPreview] = React.useState(false);
  const [previewContent, setPreviewContent] = React.useState(null);

  // FY + Job picker
  const [selectedFY, setSelectedFY] = React.useState(getCurrentFY());
  const [jobs, setJobs] = React.useState([]);
  const [selectedJobId, setSelectedJobId] = React.useState('');

  // iter-123: 3 gradient search boxes that narrow the Job picker dropdown.
  // iter-124: Mutually exclusive — typing in one auto-clears the other two so
  // the user always works against a single search axis (matches user intent).
  // The "AND" code path remains for backward-safety but is effectively a no-op
  // because at most one search has a value at any moment.
  const [jobNoSearch, setJobNoSearch] = React.useState('');
  const [partyNameSearch, setPartyNameSearch] = React.useState('');
  const [regNoSearch, setRegNoSearch] = React.useState('');

  // iter-124: setters that clear siblings — the heart of the radio-style UX.
  const setJobNoSearchExclusive = (v) => {
    setJobNoSearch(v);
    if (v) { setPartyNameSearch(''); setRegNoSearch(''); }
  };
  const setPartyNameSearchExclusive = (v) => {
    setPartyNameSearch(v);
    if (v) { setJobNoSearch(''); setRegNoSearch(''); }
  };
  const setRegNoSearchExclusive = (v) => {
    setRegNoSearch(v);
    if (v) { setJobNoSearch(''); setPartyNameSearch(''); }
  };

  const filteredJobs = React.useMemo(() => {
    const jn = jobNoSearch.trim().toLowerCase();
    const pn = partyNameSearch.trim().toLowerCase();
    const rn = regNoSearch.trim().toLowerCase();
    if (!jn && !pn && !rn) return jobs;
    return jobs.filter((j) => {
      if (jn && !String(j.job_no || '').toLowerCase().includes(jn)) return false;
      if (pn && !String(j.party_name || '').toLowerCase().includes(pn)) return false;
      if (rn && !String(j.reg_no || '').toLowerCase().includes(rn)) return false;
      return true;
    });
  }, [jobs, jobNoSearch, partyNameSearch, regNoSearch]);

  const hasActiveJobSearch = !!(jobNoSearch || partyNameSearch || regNoSearch);
  const clearAllJobSearches = () => {
    setJobNoSearch('');
    setPartyNameSearch('');
    setRegNoSearch('');
  };

  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    page_size: 'A4',
    margins: { top: 20, bottom: 20, left: 15, right: 15 },
    header_content: '',
    footer_content: '',
    body_content: '',
    logo_base64: null,
  });

  React.useEffect(() => {
    fetchTemplates();
    fetchPlaceholders();
  }, []);

  React.useEffect(() => {
    fetchJobs(selectedFY);
    setSelectedJobId(''); // reset selection when FY changes
  }, [selectedFY]);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/service/report-templates`, getAuthHeader());
      setTemplates(data || []);
    } catch {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceholders = async () => {
    try {
      const { data } = await axios.get(`${API}/service/report-templates/placeholders`, getAuthHeader());
      setPlaceholders(data || {});
      const firstKey = Object.keys(data || {})[0];
      if (firstKey) setExpandedCategories({ [firstKey]: true });
    } catch (e) {
      console.error('Failed to fetch placeholders', e);
    }
  };

  const fetchJobs = async (fy) => {
    try {
      const { data } = await axios.get(`${API}/service/report-templates/jobs`, {
        ...getAuthHeader(),
        params: { financial_year: fy },
      });
      setJobs(data?.jobs || []);
    } catch (e) {
      console.error('Failed to fetch jobs', e);
      setJobs([]);
    }
  };

  const handleInputChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));
  const handleMarginChange = (side, value) =>
    setFormData((p) => ({ ...p, margins: { ...p.margins, [side]: parseFloat(value) || 0 } }));

  const insertPlaceholder = (key) => {
    const placeholder = `{{${key}}}`;
    const fieldMap = { header: 'header_content', body: 'body_content', footer: 'footer_content' };
    const f = fieldMap[activeEditorField];
    setFormData((p) => ({ ...p, [f]: p[f] + placeholder }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) {
      toast.error('Logo must be less than 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setFormData((p) => ({ ...p, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };
  const removeLogo = () => setFormData((p) => ({ ...p, logo_base64: null }));

  const resetForm = () => {
    setFormData({
      name: '', description: '', page_size: 'A4',
      margins: { top: 20, bottom: 20, left: 15, right: 15 },
      header_content: '', footer_content: '', body_content: '',
      logo_base64: null,
    });
    setEditingTemplate(null);
    setActiveEditorField('body');
  };

  const openCreateModal = () => { resetForm(); setShowModal(true); };

  const openEditModal = (t) => {
    setEditingTemplate(t);
    setFormData({
      name: t.name, description: t.description || '',
      page_size: t.page_size || 'A4',
      margins: t.margins || { top: 20, bottom: 20, left: 15, right: 15 },
      header_content: t.header_content || '',
      footer_content: t.footer_content || '',
      body_content: t.body_content || '',
      logo_base64: t.logo_base64 || null,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      if (editingTemplate) {
        await axios.put(`${API}/service/report-templates/${editingTemplate.id}`, formData, getAuthHeader());
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/service/report-templates`, formData, getAuthHeader());
        toast.success('Template created');
      }
      setShowModal(false);
      resetForm();
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/service/report-templates/${id}`, getAuthHeader());
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    if (!selectedJobId) {
      toast.error('Pick a Job No first');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/service/report-templates/generate-preview`,
        { template_id: template.id, job_id: selectedJobId },
        getAuthHeader(),
      );
      setPreviewContent(data);
      setShowPreview(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const handlePrint = () => {
    if (!previewContent) return;
    const win = window.open('', '_blank');
    const margins = previewContent.margins || { top: 20, bottom: 20, left: 15, right: 15 };
    const pageSize = previewContent.page_size || 'A4';
    win.document.write(`
      <!DOCTYPE html><html><head>
      <title>${previewContent.template_name} - Job ${previewContent.job_no || ''}</title>
      <style>
        @page { size: ${pageSize}; margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Tahoma, Verdana, sans-serif; font-size: 12pt; line-height: 1.5; color: #333; }
        .page { display: flex; flex-direction: column; min-height: 100vh; }
        .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
        .header-row { display: flex; align-items: center; gap: 15px; }
        .logo { max-height: 60px; max-width: 150px; object-fit: contain; }
        .body-content { flex: 1; }
        .footer { border-top: 1px solid #999; padding-top: 10px; margin-top: auto; font-size: 10pt; color: #666; }
        table { width: 100%; border-collapse: collapse; margin: 10px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f5f5f5; font-weight: 600; }
        h1 { font-size: 18pt; margin-bottom: 10px; }
        h2 { font-size: 14pt; margin-bottom: 8px; }
        p { margin-bottom: 8px; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      </style></head><body>
      <div class="page">
        ${previewContent.header_content ? `
          <div class="header"><div class="header-row">
            ${previewContent.logo_base64 ? `<img class="logo" src="${previewContent.logo_base64}" />` : ''}
            <div>${previewContent.header_content}</div>
          </div></div>` : ''}
        <div class="body-content">${previewContent.body_content}</div>
        ${previewContent.footer_content ? `<div class="footer">${previewContent.footer_content}</div>` : ''}
      </div>
      <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  const toggleCategory = (key) =>
    setExpandedCategories((p) => ({ ...p, [key]: !p[key] }));

  if (loading) {
    return (
      <ServiceLayout user={user} onLogout={onLogout} title="Multi Reports">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
        </div>
      </ServiceLayout>
    );
  }

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="Multi Reports" subtitle="Custom printable templates powered by job & master data">
      <div className="max-w-7xl mx-auto space-y-5" data-testid="service-multi-reports">
        {/* Header */}
        <div className="bg-white/85 backdrop-blur rounded-2xl border border-white/60 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" />
                Templates
              </h2>
              <p className="text-[12.5px] text-slate-500 mt-0.5">Build reusable A4/A5 documents with placeholders pulled from masters and job fields.</p>
            </div>
            <button
              onClick={openCreateModal}
              data-testid="create-template-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-[13px] font-medium shadow-md hover:opacity-90 transition-all"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          </div>
        </div>

        {/* iter-123: Gradient search row — 3 distinct accents, purely client-side
            filter on the loaded jobs[]. The existing FY + Job picker below is
            untouched; we only swap its source array to filteredJobs. */}
        <div className="bg-white/85 backdrop-blur rounded-2xl border border-white/60 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex w-1.5 h-5 rounded-full bg-gradient-to-b from-indigo-500 via-fuchsia-500 to-cyan-500"></span>
              <span className="text-[12.5px] font-semibold text-slate-700 tracking-tight">Search Jobs</span>
              {hasActiveJobSearch && (
                <span className="ml-2 px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600" data-testid="job-search-count">
                  {filteredJobs.length} of {jobs.length}
                </span>
              )}
            </div>
            {hasActiveJobSearch && (
              <button
                type="button"
                onClick={clearAllJobSearches}
                className="text-[11.5px] font-medium text-slate-500 hover:text-slate-800 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="job-search-clear"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Job No — Indigo → Violet */}
            <div className="group relative">
              <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 opacity-25 group-hover:opacity-60 group-focus-within:opacity-100 blur-[1px] transition-opacity duration-240 ease-out pointer-events-none"></div>
              <div className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 backdrop-blur-sm border border-indigo-100 group-focus-within:border-transparent transition-all duration-180 ease-out group-hover:-translate-y-px group-hover:shadow-[0_6px_18px_-6px_rgba(99,102,241,0.35)]">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 text-white shrink-0 shadow-[0_2px_6px_rgba(99,102,241,0.40)]">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                </span>
                <input
                  type="text"
                  value={jobNoSearch}
                  onChange={(e) => setJobNoSearchExclusive(e.target.value)}
                  placeholder="Job No"
                  data-testid="search-job-no"
                  className="flex-1 bg-transparent outline-none text-[13px] text-slate-800 placeholder:text-slate-400"
                />
                {jobNoSearch && (
                  <button type="button" onClick={() => setJobNoSearch('')} className="text-slate-400 hover:text-indigo-600 text-[15px] leading-none px-1" aria-label="Clear Job No search">×</button>
                )}
              </div>
            </div>
            {/* Party's Name — Violet → Pink */}
            <div className="group relative">
              <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 opacity-25 group-hover:opacity-60 group-focus-within:opacity-100 blur-[1px] transition-opacity duration-240 ease-out pointer-events-none"></div>
              <div className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 backdrop-blur-sm border border-violet-100 group-focus-within:border-transparent transition-all duration-180 ease-out group-hover:-translate-y-px group-hover:shadow-[0_6px_18px_-6px_rgba(168,85,247,0.35)]">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-pink-500 text-white shrink-0 shadow-[0_2px_6px_rgba(168,85,247,0.40)]">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <input
                  type="text"
                  value={partyNameSearch}
                  onChange={(e) => setPartyNameSearchExclusive(e.target.value)}
                  placeholder="Party's Name"
                  data-testid="search-party-name"
                  className="flex-1 bg-transparent outline-none text-[13px] text-slate-800 placeholder:text-slate-400"
                />
                {partyNameSearch && (
                  <button type="button" onClick={() => setPartyNameSearch('')} className="text-slate-400 hover:text-pink-600 text-[15px] leading-none px-1" aria-label="Clear Party Name search">×</button>
                )}
              </div>
            </div>
            {/* Reg No — Cyan → Indigo */}
            <div className="group relative">
              <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 opacity-25 group-hover:opacity-60 group-focus-within:opacity-100 blur-[1px] transition-opacity duration-240 ease-out pointer-events-none"></div>
              <div className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 backdrop-blur-sm border border-cyan-100 group-focus-within:border-transparent transition-all duration-180 ease-out group-hover:-translate-y-px group-hover:shadow-[0_6px_18px_-6px_rgba(6,182,212,0.35)]">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-cyan-500 to-indigo-600 text-white shrink-0 shadow-[0_2px_6px_rgba(6,182,212,0.40)]">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 10h2M7 14h6"/></svg>
                </span>
                <input
                  type="text"
                  value={regNoSearch}
                  onChange={(e) => setRegNoSearchExclusive(e.target.value)}
                  placeholder="Reg. No"
                  data-testid="search-reg-no"
                  className="flex-1 bg-transparent outline-none text-[13px] text-slate-800 placeholder:text-slate-400"
                />
                {regNoSearch && (
                  <button type="button" onClick={() => setRegNoSearch('')} className="text-slate-400 hover:text-cyan-600 text-[15px] leading-none px-1" aria-label="Clear Reg No search">×</button>
                )}
              </div>
            </div>
          </div>

          {/* iter-124: Inline matched-jobs list — only when search is active.
              Lets the user click a result instantly without opening the dropdown.
              Capped at 8 cards; 9+ matches show a hint to use the dropdown.
              Clicking a card sets selectedJobId AND clears the active search so
              the UI returns to a clean "selected job" state. */}
          {hasActiveJobSearch && filteredJobs.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="job-search-results">
              <p className="text-[11px] font-medium text-slate-500 mb-2 tracking-wide uppercase">
                {filteredJobs.length === 1 ? '1 match' : `${Math.min(filteredJobs.length, 8)} of ${filteredJobs.length} matches`}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {filteredJobs.slice(0, 8).map((j) => {
                  const isSelected = j.id === selectedJobId;
                  return (
                    <button
                      key={j.id}
                      type="button"
                      onClick={() => {
                        setSelectedJobId(j.id);
                        clearAllJobSearches();
                      }}
                      data-testid={`job-result-${j.id}`}
                      className={`group text-left px-3 py-2.5 rounded-xl border transition-all duration-180 ease-out hover:-translate-y-px ${
                        isSelected
                          ? 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50 shadow-[0_4px_14px_-6px_rgba(99,102,241,0.45)]'
                          : 'border-slate-200/80 bg-white/95 hover:border-indigo-200 hover:shadow-[0_4px_14px_-6px_rgba(99,102,241,0.25)]'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[12.5px] font-semibold tracking-tight ${isSelected ? 'text-indigo-700' : 'text-slate-800 group-hover:text-indigo-700'}`}>
                          #{j.job_no}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-indigo-600 text-white">Selected</span>
                        )}
                      </div>
                      <p className="text-[12px] text-slate-700 truncate">{j.party_name || '—'}</p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {j.reg_no || j.model || ''}
                      </p>
                    </button>
                  );
                })}
              </div>
              {filteredJobs.length > 8 && (
                <p className="text-[11px] text-slate-500 mt-2 text-center">
                  Showing first 8 — refine your search or use the dropdown below to see all {filteredJobs.length} matches.
                </p>
              )}
            </div>
          )}
        </div>

        {/* FY + Job picker */}
        <div className="bg-white/85 backdrop-blur rounded-2xl border border-white/60 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2">
              <label className="text-[12px] font-medium text-slate-500">FY</label>
              <select
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                data-testid="fy-select"
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              >
                {fyOptions().map((fy) => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Select Job No for Preview</label>
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                data-testid="job-select"
                className="w-full max-w-md px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="">— Select a Job —</option>
                {filteredJobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    #{j.job_no} • {j.party_name || '—'} • {j.reg_no || j.model || ''}
                  </option>
                ))}
              </select>
              {jobs.length === 0 && (
                <p className="text-[11.5px] text-amber-600 mt-1">No jobs in FY {selectedFY}</p>
              )}
              {jobs.length > 0 && filteredJobs.length === 0 && (
                <p className="text-[11.5px] text-amber-600 mt-1" data-testid="no-search-results">No jobs match the current search filters</p>
              )}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className="bg-white/85 backdrop-blur rounded-2xl border border-white/60 p-10 text-center" data-testid="empty-state">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-[14px] font-semibold text-slate-700">No templates yet</h3>
            <p className="text-[12.5px] text-slate-500 mt-1">Create your first Multi Report template to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((t) => (
              <div key={t.id} className="bg-white/90 backdrop-blur rounded-2xl border border-white/60 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50/40 to-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-[14px] font-semibold text-slate-800 truncate">{t.name}</h3>
                      <p className="text-[12px] text-slate-500 mt-0.5 line-clamp-2">{t.description || 'No description'}</p>
                    </div>
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10.5px] font-medium rounded-md">{t.page_size}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[11px] text-slate-400 mb-3">
                    Created {new Date(t.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePreview(t)}
                      disabled={!selectedJobId}
                      data-testid={`preview-btn-${t.id}`}
                      title={!selectedJobId ? 'Pick a Job first' : 'Preview'}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-[12px] font-medium hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview
                    </button>
                    <button
                      onClick={() => openEditModal(t)}
                      data-testid={`edit-btn-${t.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-sky-50 text-sky-700 rounded-lg text-[12px] font-medium hover:bg-sky-100 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(t.id)}
                      data-testid={`delete-btn-${t.id}`}
                      className="px-3 py-2 bg-rose-50 text-rose-700 rounded-lg text-[12px] font-medium hover:bg-rose-100 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto py-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 my-4" data-testid="template-modal">
              <div className="flex items-center justify-between p-4 border-b text-white rounded-t-2xl"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}>
                <h2 className="text-[16px] font-semibold">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h2>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  data-testid="close-modal-btn"
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto">
                  {/* Left: settings + placeholders */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Template Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        data-testid="tpl-name-input"
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        placeholder="e.g., Job Card / Work Order"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        data-testid="tpl-description-input"
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        rows={2}
                        placeholder="Brief description..."
                      />
                    </div>

                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Page Size</label>
                      <select
                        value={formData.page_size}
                        onChange={(e) => handleInputChange('page_size', e.target.value)}
                        data-testid="tpl-page-size"
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                      >
                        {Object.entries(PAGE_SIZES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-2">Margins (mm)</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['top', 'bottom', 'left', 'right'].map((side) => (
                          <div key={side}>
                            <label className="text-[11px] text-slate-500 capitalize">{side}</label>
                            <input
                              type="number"
                              value={formData.margins[side]}
                              onChange={(e) => handleMarginChange(side, e.target.value)}
                              data-testid={`tpl-margin-${side}`}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-[12px]"
                              min="0" max="50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-2">Logo (Optional)</label>
                      {formData.logo_base64 ? (
                        <div className="relative inline-block">
                          <img src={formData.logo_base64} alt="Logo" className="h-16 object-contain border rounded-lg p-1" />
                          <button
                            type="button"
                            onClick={removeLogo}
                            data-testid="remove-logo-btn"
                            className="absolute -top-2 -right-2 p-1 bg-rose-500 text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                          <Upload className="w-4 h-4 text-slate-400" />
                          <span className="text-[12px] text-slate-500">Upload Logo</span>
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                        </label>
                      )}
                      <p className="text-[10.5px] text-slate-400 mt-1">Max 500KB · PNG/JPG</p>
                    </div>

                    <div>
                      <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Insert Placeholders</label>
                      <p className="text-[11px] text-slate-500 mb-2">Click to insert into the active editor</p>
                      <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50/50" data-testid="placeholders-panel">
                        {Object.entries(placeholders).map(([key, cat]) => (
                          <PlaceholderCategory
                            key={key}
                            category={cat.label}
                            fields={cat.fields}
                            onInsert={insertPlaceholder}
                            isExpanded={!!expandedCategories[key]}
                            onToggle={() => toggleCategory(key)}
                            testId={`ph-cat-${key}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: editors */}
                  <div className="lg:col-span-2 space-y-4">
                    <div className="flex gap-2 border-b border-slate-200 pb-2">
                      {[
                        { key: 'header', label: 'Header' },
                        { key: 'body', label: 'Body Content' },
                        { key: 'footer', label: 'Footer' },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveEditorField(tab.key)}
                          data-testid={`editor-tab-${tab.key}`}
                          className={`px-4 py-1.5 rounded-lg text-[12.5px] font-medium transition-colors ${
                            activeEditorField === tab.key
                              ? 'bg-indigo-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {activeEditorField === 'header' && (
                      <div>
                        <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Header Content (top of each page)</label>
                        <textarea
                          value={formData.header_content}
                          onChange={(e) => handleInputChange('header_content', e.target.value)}
                          data-testid="tpl-header-textarea"
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl font-mono text-[12px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                          rows={6}
                          placeholder="<h1>{{company_name}}</h1>&#10;<p>{{company_address}}</p>&#10;<p>Tel: {{company_mobile}} | Email: {{company_email}}</p>"
                        />
                      </div>
                    )}

                    {activeEditorField === 'body' && (
                      <div>
                        <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Body Content (main document)</label>
                        <textarea
                          value={formData.body_content}
                          onChange={(e) => handleInputChange('body_content', e.target.value)}
                          data-testid="tpl-body-textarea"
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl font-mono text-[12px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                          rows={15}
                          placeholder={'<h2>JOB CARD</h2>\n<p>Job No: {{job_no}} | FY: {{financial_year}}</p>\n<p>Date: {{job_date}}</p>\n<h3>Customer</h3>\n<p>{{party_name}} | {{mobile_no_1}}</p>\n<h3>Vehicle</h3>\n<table>\n  <tr><th>Reg No</th><td>{{reg_no}}</td></tr>\n  <tr><th>Brand / Model</th><td>{{brand_name}} {{model}}</td></tr>\n  <tr><th>Chassis</th><td>{{chassis_no}}</td></tr>\n</table>\n<h3>Service</h3>\n<p>Status: {{status}} | Service Head: {{service_head}}</p>\n<p>Job Type: {{job_type}} | Supervisor: {{supervisor}} | Technician: {{technician}}</p>\n<p>{{customer_voice}}</p>'}
                        />
                      </div>
                    )}

                    {activeEditorField === 'footer' && (
                      <div>
                        <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Footer Content (bottom of each page)</label>
                        <textarea
                          value={formData.footer_content}
                          onChange={(e) => handleInputChange('footer_content', e.target.value)}
                          data-testid="tpl-footer-textarea"
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl font-mono text-[12px] focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                          rows={6}
                          placeholder="<p>This is a computer-generated document.</p>&#10;<p>Generated on {{current_date}} at {{current_time}}</p>"
                        />
                      </div>
                    )}

                    <p className="text-[11px] text-slate-500">
                      Supports HTML: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;table&gt;, &lt;tr&gt;, &lt;th&gt;, &lt;td&gt;, &lt;strong&gt;, &lt;em&gt;, etc.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 p-4 border-t bg-slate-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    data-testid="cancel-template-btn"
                    className="px-4 py-2 border border-slate-200 rounded-xl text-[13px] hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="submit-template-btn"
                    className="px-5 py-2 text-white rounded-xl text-[13px] font-medium shadow-md hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview modal */}
        {showPreview && previewContent && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-testid="preview-modal">
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h2 className="text-[15px] font-semibold text-slate-800">Preview · {previewContent.template_name}</h2>
                  <p className="text-[12px] text-slate-500">
                    Job #{previewContent.job_no || '—'} · FY {previewContent.financial_year || '—'} · {previewContent.customer_name || '—'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrint}
                    data-testid="print-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-[13px] hover:bg-emerald-600"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    data-testid="close-preview-btn"
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-slate-100">
                <div
                  className="bg-white mx-auto shadow-lg"
                  style={{
                    width: previewContent.page_size === 'A5' ? '370px' : '595px',
                    minHeight: previewContent.page_size === 'A5' ? '525px' : '842px',
                    padding: `${previewContent.margins?.top || 20}px ${previewContent.margins?.right || 15}px ${previewContent.margins?.bottom || 20}px ${previewContent.margins?.left || 15}px`,
                  }}
                >
                  {previewContent.header_content && (
                    <div className="border-b-2 border-slate-800 pb-3 mb-4">
                      <div className="flex items-center gap-4">
                        {previewContent.logo_base64 && (
                          <img src={previewContent.logo_base64} alt="Logo" className="h-12 object-contain" />
                        )}
                        <div className="preview-html" dangerouslySetInnerHTML={{ __html: previewContent.header_content }} />
                      </div>
                    </div>
                  )}
                  <div className="preview-html" dangerouslySetInnerHTML={{ __html: previewContent.body_content }} />
                  {previewContent.footer_content && (
                    <div className="border-t border-slate-300 pt-3 mt-4 text-[12px] text-slate-600">
                      <div className="preview-html" dangerouslySetInnerHTML={{ __html: previewContent.footer_content }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .preview-html h1 { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
          .preview-html h2 { font-size: 16px; font-weight: bold; margin-bottom: 6px; margin-top: 12px; }
          .preview-html h3 { font-size: 14px; font-weight: bold; margin-bottom: 4px; margin-top: 10px; }
          .preview-html p { margin-bottom: 6px; font-size: 12px; }
          .preview-html table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
          .preview-html th, .preview-html td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          .preview-html th { background-color: #f5f5f5; font-weight: 600; }
          .preview-html strong { font-weight: bold; }
          .preview-html em { font-style: italic; }
          .preview-html ul, .preview-html ol { margin-left: 20px; margin-bottom: 8px; }
          .preview-html li { margin-bottom: 4px; font-size: 12px; }
        `}</style>
      </div>
    </ServiceLayout>
  );
}
